import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import React from "react";
const CallToAction = React.lazy(() => import("../components/CallToAction"));

// ─── Types ────────────────────────────────────────────────────────────────────

type TextNode = {
  type: "text";
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

type BlockNode = {
  type: string;
  children?: (TextNode | BlockNode)[];
  level?: number;
  format?: "ordered" | "unordered";
  url?: string;
  image?: { url: string; alternativeText?: string };
};

type TagIcone = "tag" | "folha" | "coracao" | "cerebro" | "ciencia" | "sol" | "lua" | "nutricao" | "legislacao" | "entrevista";
type Tag = { id: number; Nome: string; Icone?: TagIcone };

type Blog = {
  id: number;
  Titulo?: string;
  Slug?: string;
  Resumo?: string;
  Conteudo?: BlockNode[];
  Data?: string;
  Categoria?: string;
  TempoLeitura?: number;
  Tags?: Tag[];
  Capa?: {
    url?: string;
    formats?: {
      large?: { url?: string };
      medium?: { url?: string };
      small?: { url?: string };
    };
  };
  seo?: any;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function extractHeadings(blocks?: BlockNode[]) {
  if (!blocks) return [];
  return blocks
    .filter((b) => b.type === "heading" && (b.level === 2 || b.level === 3))
    .map((b) => {
      const text =
        b.children
          ?.filter((c) => c.type === "text")
          .map((c) => (c as TextNode).text)
          .join("") ?? "";
      return { id: slugify(text), text, level: b.level ?? 2 };
    })
    .filter((h) => h.text);
}

function readingTime(blog: Blog): number {
  if (blog.TempoLeitura) return blog.TempoLeitura;
  const words =
    blog.Conteudo?.flatMap(
      (b) =>
        b.children?.filter((c) => c.type === "text").map((c) => (c as TextNode).text) ?? []
    )
      .join(" ")
      .split(/\s+/)
      .filter(Boolean).length ?? 0;
  return Math.max(1, Math.round(words / 130));
}

function getImgSrc(blog: Blog, base: string): string | undefined {
  const url =
    blog.Capa?.formats?.large?.url ??
    blog.Capa?.formats?.medium?.url ??
    blog.Capa?.formats?.small?.url ??
    blog.Capa?.url;
  if (!url) return undefined;
  return url.startsWith("http") ? url : `${base}${url}`;
}

// ─── Badge colors (outline style for post page) ───────────────────────────────

const CATEGORY_STYLE: Record<string, string> = {
  Artigo:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Notícia":  "bg-sky-50 text-sky-700 border-sky-200",
  Estudo:     "bg-violet-50 text-violet-700 border-violet-200",
  Dicas:      "bg-amber-50 text-amber-700 border-amber-200",
  Receitas:   "bg-rose-50 text-rose-700 border-rose-200",
  Entrevista: "bg-teal-50 text-teal-700 border-teal-200",
};

const TAG_PALETTE_OUTLINE = [
  "bg-green-50 text-green-700 border-green-200",
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-sky-50 text-sky-700 border-sky-200",
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-pink-50 text-pink-700 border-pink-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-orange-50 text-orange-700 border-orange-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-lime-50 text-lime-700 border-lime-200",
];

function tagOutlineColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return TAG_PALETTE_OUTLINE[Math.abs(h) % TAG_PALETTE_OUTLINE.length];
}

// ─── Tag icon ─────────────────────────────────────────────────────────────────

function TagIcon({ icone }: { icone?: TagIcone }) {
  switch (icone) {
    case "folha":      return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 13C7 13 1 9 1 4a6 6 0 0 1 12 0c0 5-6 9-6 9z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M7 4v9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case "coracao":    return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 12S1 8 1 4.5a3.5 3.5 0 0 1 6-2.45A3.5 3.5 0 0 1 13 4.5C13 8 7 12 7 12z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>;
    case "cerebro":    return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M5 12V9a4 4 0 1 1 4 0v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
    case "ciencia":    return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M5 1v5L2 11a1 1 0 0 0 .9 1.5h8.2A1 1 0 0 0 12 11L9 6V1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 1h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
    case "sol":        return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.3"/><path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M3 3l1 1M10 10l1 1M3 11l1-1M10 4l1-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
    case "lua":        return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M11.5 9A5.5 5.5 0 0 1 5 2.5a5.5 5.5 0 1 0 6.5 6.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>;
    case "nutricao":   return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1C5 1 2 3 2 6c0 3.3 2.7 6 5 7 2.3-1 5-3.7 5-7 0-3-3-5-5-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M7 4v6M5 6h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case "legislacao": return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M4 4l-3 4h6L4 4zM10 6l-3 4h6l-3-4z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "entrevista": return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M2 2h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5l-3 2V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
    default:           return <svg width="11" height="11" viewBox="0 0 10 10" fill="none"><path d="M1 1h4l4 4-4 4-4-4V1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><circle cx="3" cy="3" r="0.8" fill="currentColor"/></svg>;
  }
}

// ─── Block renderer ───────────────────────────────────────────────────────────

function renderText(node: TextNode, key: number): React.ReactNode {
  let el: React.ReactNode = node.text;
  if (node.bold)          el = <strong key={key} className="font-semibold">{el}</strong>;
  if (node.italic)        el = <em key={key}>{el}</em>;
  if (node.underline)     el = <u key={key}>{el}</u>;
  if (node.strikethrough) el = <s key={key}>{el}</s>;
  if (node.code)          el = <code key={key} className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800">{el}</code>;
  return el;
}

function renderBlock(node: BlockNode, key: number, base: string): React.ReactNode {
  const children = node.children?.map((c, i) =>
    c.type === "text" ? renderText(c as TextNode, i) : renderBlock(c as BlockNode, i, base)
  );

  switch (node.type) {
    case "paragraph":
      return <p key={key} className="mb-5 text-[15px] leading-[1.85] text-gray-700">{children}</p>;

    case "heading": {
      const level = node.level ?? 2;
      const text  = node.children?.filter((c) => c.type === "text").map((c) => (c as TextNode).text).join("") ?? "";
      const id    = slugify(text);
      const Tag   = `h${level}` as React.ElementType;
      const cls: Record<number, string> = {
        1: "text-[26px] font-light text-gray-800 mt-10 mb-4",
        2: "text-[22px] font-light text-gray-800 mt-8 mb-3",
        3: "text-[18px] font-medium text-gray-800 mt-6 mb-2",
        4: "text-[16px] font-medium text-gray-700 mt-5 mb-2",
        5: "text-[15px] font-medium text-gray-700 mt-4 mb-1",
        6: "text-[14px] font-medium text-gray-600 mt-4 mb-1",
      };
      return <Tag key={key} id={id} className={`font-display ${cls[level] ?? cls[2]}`}>{children}</Tag>;
    }

    case "list":
      return node.format === "ordered"
        ? <ol key={key} className="list-decimal list-outside ml-5 mb-5 space-y-2 text-gray-700 text-[15px]">{children}</ol>
        : <ul key={key} className="list-disc list-outside ml-5 mb-5 space-y-2 text-gray-700 text-[15px]">{children}</ul>;

    case "list-item":
      return <li key={key} className="leading-relaxed">{children}</li>;

    case "quote":
      return (
        <blockquote key={key} className="border-l-4 border-primary-darker pl-5 italic text-gray-600 mb-5 py-1 text-[15px]">
          {children}
        </blockquote>
      );

    case "code":
      return (
        <pre key={key} className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto text-sm font-mono mb-5">
          {children}
        </pre>
      );

    case "image": {
      const raw = node.image?.url;
      const src = raw ? (raw.startsWith("http") ? raw : `${base}${raw}`) : undefined;
      return src ? (
        <figure key={key} className="my-8">
          <img src={src} alt={node.image?.alternativeText ?? ""} className="rounded-xl w-full object-cover" />
        </figure>
      ) : null;
    }

    case "link":
      return (
        <a key={key} href={node.url} className="text-primary-darker underline underline-offset-2 hover:opacity-75 transition-opacity">
          {children}
        </a>
      );

    default:
      return <span key={key}>{children}</span>;
  }
}

// ─── Related blog card ────────────────────────────────────────────────────────

const CATEGORY_COLORS_SOLID: Record<string, string> = {
  Artigo:     "bg-emerald-500",
  "Notícia":  "bg-sky-500",
  Estudo:     "bg-violet-500",
  Dicas:      "bg-amber-500",
  Receitas:   "bg-rose-500",
  Entrevista: "bg-teal-500",
};

const TAG_PALETTE_SOLID = [
  "bg-green-500","bg-teal-500","bg-sky-500","bg-indigo-500",
  "bg-violet-500","bg-pink-500","bg-rose-500","bg-orange-500","bg-amber-500","bg-lime-500",
];

function tagSolidColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return TAG_PALETTE_SOLID[Math.abs(h) % TAG_PALETTE_SOLID.length];
}

function RelatedCard({ b, base }: { b: Blog; base: string }) {
  const src = getImgSrc(b, base);
  const mins = readingTime(b);
  const allBadges = [
    ...(b.Categoria ? [{ key: `cat-${b.id}`, label: b.Categoria, cls: CATEGORY_COLORS_SOLID[b.Categoria] ?? "bg-gray-500" }] : []),
    ...(b.Tags ?? []).map((t) => ({ key: `tag-${t.id}`, label: t.Nome, cls: tagSolidColor(t.Nome) })),
  ];

  return (
    <Link
      to={`/blogs/${b.Slug}`}
      className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex-shrink-0"
    >
      <div className="relative overflow-hidden aspect-[16/9]">
        {src ? (
          <img
            src={src}
            alt={b.Titulo ?? ""}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}
        {allBadges.length > 0 && (
          <div className="absolute bottom-2 left-2 flex flex-wrap gap-1">
            {allBadges.slice(0, 3).map((b) => (
              <span key={b.key} className={`${b.cls} text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full`}>
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <p className="font-display font-light text-[15px] leading-snug text-gray-800 line-clamp-2 group-hover:text-primary-darker transition-colors">
          {b.Titulo}
        </p>
        <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-auto pt-1">
          {b.Data && (
            <span className="flex items-center gap-1">
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M1 6h12M4.5 1v2M9.5 1v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              {new Date(b.Data).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })}
            </span>
          )}
          <span className="flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M7 4v3.5l2.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {mins} min
          </span>
        </div>
        <span className="text-[12px] text-primary-darker font-medium mt-1 group-hover:underline underline-offset-2">
          Leia mais sobre →
        </span>
      </div>
    </Link>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BlogPost() {
  const { slug }  = useParams();
  const [blog, setBlog]       = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated]         = useState<Blog[]>([]);
  const [relIdx, setRelIdx]           = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [slideWidth, setSlideWidth]   = useState(0);
  const [activeId, setActiveId]       = useState("");
  const carouselRef = useRef<HTMLDivElement>(null);
  const base = import.meta.env.VITE_API_URL;

  useEffect(() => {
    let alive = true;
    if (!slug) return;
    setLoading(true);

    fetch(`${base}/api/blogs?filters[Slug][$eq]=${encodeURIComponent(slug)}&populate=*`)
      .then((r) => r.json())
      .then((data) => { if (alive) setBlog(data?.data?.[0] ?? null); })
      .catch(() => { if (alive) setBlog(null); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [base, slug]);

  useEffect(() => {
    let alive = true;
    fetch(`${base}/api/blogs?sort=Data:desc&populate=Capa,Tags&pagination[pageSize]=20`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        const all: Blog[] = data?.data ?? [];
        setRelated(all.filter((b) => b.Slug !== slug).slice(0, 5));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [base, slug]);

  // Measure carousel container and update slide step on resize
  useLayoutEffect(() => {
    const GAP = 16;
    const update = () => {
      if (!carouselRef.current) return;
      const W = carouselRef.current.clientWidth;
      const v = W >= 1024 ? 3 : W >= 640 ? 2 : 1;
      setVisibleCount(v);
      setSlideWidth((W - GAP * (v - 1)) / v + GAP);
      setRelIdx((i) => Math.min(i, Math.max(0, related.length - v)));
    };
    update();
    const ro = new ResizeObserver(update);
    if (carouselRef.current) ro.observe(carouselRef.current);
    return () => ro.disconnect();
  }, [related.length]);

  // Highlight active TOC heading while scrolling
  useEffect(() => {
    if (!blog) return;
    const hs = extractHeadings(blog.Conteudo);
    if (!hs.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (hit) setActiveId(hit.target.id);
      },
      { rootMargin: "-10% 0px -70% 0px", threshold: 0 },
    );
    hs.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [blog]);

  const imgSrc = blog ? getImgSrc(blog, base) : undefined;
  const headings = extractHeadings(blog?.Conteudo);
  const mins = blog ? readingTime(blog) : 0;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-10 sm:pt-16 md:pt-[68px] lg:pt-[74px]">

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-32">
            <span className="w-9 h-9 border-2 border-primary-darker border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Not found */}
        {!loading && !blog && (
          <div className="mx-auto max-w-3xl px-4 py-20 text-center">
            <p className="text-gray-400 text-sm mb-6">Post não encontrado.</p>
            <Link to="/blogs" className="text-primary-darker text-sm font-medium underline underline-offset-2">
              ← Voltar para o Blog
            </Link>
          </div>
        )}

        {/* Article */}
        {!loading && blog && (
          <div className="sm:mx-auto sm:max-w-[90%] px-4 py-8 sm:py-12">

            {/* ── Breadcrumb ── */}
            <nav className="flex flex-wrap items-center gap-1.5 text-sm text-gray-400 mb-7" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-darker transition-colors flex items-center">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 6l6-5 6 5v7a1 1 0 0 1-1 1H9v-4H5v4H2a1 1 0 0 1-1-1V6z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                </svg>
              </Link>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <Link to="/blogs" className="hover:text-primary-darker transition-colors">Blog</Link>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-gray-500 line-clamp-1 max-w-[280px] sm:max-w-none">{blog.Titulo}</span>
            </nav>

            {/* ── Badges ── */}
            {(blog.Categoria || (blog.Tags && blog.Tags.length > 0)) && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {blog.Categoria && (
                  <span className={`inline-flex items-center gap-1.5 border text-[11px] font-sans font-semibold tracking-wider uppercase px-3 py-1 rounded-full ${CATEGORY_STYLE[blog.Categoria] ?? "bg-gray-50 text-gray-600 border-gray-200"}`}>
                    {blog.Categoria}
                  </span>
                )}
                {blog.Tags?.map((t) => (
                  <span
                    key={t.id}
                    className={`inline-flex items-center gap-1.5 border text-[11px] font-sans font-semibold tracking-wider uppercase px-3 py-1 rounded-full ${tagOutlineColor(t.Nome)}`}
                  >
                    <TagIcon icone={t.Icone} />
                    {t.Nome}
                  </span>
                ))}
              </div>
            )}

            {/* ── Title ── */}
            <h1 className="font-display font-light text-[26px] sm:text-[32px] leading-[1.25] text-gray-800 mb-4 max-w-4xl">
              {blog.Titulo}
            </h1>

            {/* ── Meta ── */}
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-8">
              {blog.Data && (
                <span className="flex items-center gap-1.5">
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <rect x="1" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M1 6h12M4.5 1v2M9.5 1v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                  {new Date(blog.Data).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </span>
              )}
              <span className="text-gray-200">|</span>
              <span className="flex items-center gap-1.5">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M7 4v3.5l2.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {mins} min de leitura
              </span>
            </div>

            {/* ── Two-column body ── */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

              {/* Left: image + content */}
              <div className="flex-1 min-w-0">
                {/* Cover image */}
                {imgSrc && (
                  <img
                    src={imgSrc}
                    alt={blog.Titulo ?? ""}
                    className="w-full aspect-video object-cover rounded-2xl mb-8"
                    fetchPriority="high"
                    decoding="async"
                  />
                )}

                {/* Resumo */}
                {blog.Resumo && (
                  <p className="text-base text-gray-600 leading-relaxed mb-6 border-l-4 border-primary-darker pl-4 italic">
                    {blog.Resumo}
                  </p>
                )}

                {/* Body */}
                <article id="article-title" className="prose-content">
                  {blog.Conteudo?.map((block, i) => renderBlock(block, i, base))}
                </article>
              </div>

              {/* Right: TOC sidebar */}
              {headings.length > 0 && (
                <aside className="hidden lg:block w-[280px] xl:w-[300px] flex-shrink-0 sticky top-[80px] self-start">
                  <div className="border-l-4 border-primary-darker pl-5">
                    <p className="font-display font-semibold text-gray-800 text-[15px] leading-snug mb-4">
                      {blog.Titulo}
                    </p>
                    <nav>
                      <ul className="space-y-3">
                        {headings.map((h) => (
                          <li key={h.id}>
                            <a
                              href={`#${h.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                const el = document.getElementById(h.id);
                                if (!el) return;
                                window.scrollTo({
                                  top: el.getBoundingClientRect().top + window.scrollY - 90,
                                  behavior: "smooth",
                                });
                              }}
                              className={`block text-[13px] leading-snug transition-colors cursor-pointer ${h.level === 3 ? "pl-3" : ""} ${
                              h.id === activeId
                                ? "text-primary-darker font-medium underline underline-offset-4 decoration-primary-darker/40"
                                : "text-gray-500 hover:text-primary-darker"
                            }`}
                            >
                              {h.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </aside>
              )}
            </div>

          </div>
        )}

        {/* ── Ver mais artigos ── */}
        {related.length > 0 && (
          <section className="sm:mx-auto sm:max-w-[90%] px-4 pb-10 pt-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-light text-[22px] sm:text-[26px] text-gray-800">
                Ver mais artigos
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Anterior"
                  onClick={() => setRelIdx((i) => Math.max(0, i - 1))}
                  disabled={relIdx === 0}
                  className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-primary-darker hover:text-primary-darker transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Próximo"
                  onClick={() => setRelIdx((i) => Math.min(related.length - visibleCount, i + 1))}
                  disabled={relIdx >= related.length - visibleCount}
                  className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-primary-darker hover:text-primary-darker transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div ref={carouselRef} className="overflow-hidden">
              <motion.div
                className="flex"
                style={{ gap: 16 }}
                animate={{ x: -(relIdx * slideWidth) }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {related.map((b) => (
                  <div
                    key={b.id}
                    className="flex-shrink-0"
                    style={{ width: slideWidth > 0 ? slideWidth - 16 : undefined }}
                  >
                    <RelatedCard b={b} base={base} />
                  </div>
                ))}
              </motion.div>
            </div>

            <div className="mt-8 flex justify-center">
              <Link
                to="/blogs"
                className="inline-flex items-center gap-2 border border-primary-darker text-primary-darker text-sm font-sans font-medium px-6 py-2.5 rounded-full hover:bg-primary-darker hover:text-white transition-colors"
              >
                Ver todos os artigos
              </Link>
            </div>
          </section>
        )}

        <section className="mx-4 py-10">
          <CallToAction buttonLabel="Iniciar agora" href="#protocolo" />
        </section>

        <Footer />
      </main>
    </div>
  );
}
